import { useApi } from "@platform/api";
import { useNotification } from "@platform/hooks";
import { useMutation, useQueryClient } from "@tanstack/react-query";

interface UploadCensusResponse {
  documentId: number;
  filename: string;
  size: number;
}

interface UseCensusUploadReturn {
  upload: (file: File) => void;
  isUploading: boolean;
}

/**
 * Uploads a census for an RFP and asks for it to be read.
 *
 * The endpoint answers once the file is stored and reading has started, not
 * once the census exists — reading is a workflow. The caller refetches the
 * validation query afterwards, which is empty until the read finishes.
 */
function useCensusUpload(
  rfpId: string,
  onUploaded?: (documentId: number) => void,
): UseCensusUploadReturn {
  const axiosInstance = useApi();
  const queryClient = useQueryClient();
  const { showError, showInfo } = useNotification();

  const mutation = useMutation({
    mutationFn: async (file: File) => {
      const body = new FormData();
      body.append("file", file, file.name);

      const response = await axiosInstance.post<UploadCensusResponse>(
        `/bp/v1/rfps/${rfpId}/census-document`,
        body,
        { headers: { "Content-Type": "multipart/form-data" } },
      );

      return response.data;
    },
    onSuccess: (uploaded) => {
      // Reading has only started, so this says the file arrived and nothing
      // more. What the read made of it is reported when it finishes.
      showInfo(`${uploaded.filename} uploaded. Reading it now…`);

      onUploaded?.(uploaded.documentId);

      void queryClient.invalidateQueries({
        queryKey: ["censusValidation", rfpId],
      });
    },
    onError: () => {
      showError("The census could not be uploaded.");
    },
  });

  return {
    upload: mutation.mutate,
    isUploading: mutation.isPending,
  };
}

export { useCensusUpload };
